import { useState } from "react";
import AppShell from "@/components/layout/AppShell";
import useAutoRefresh from "@/hooks/useAutoRefresh";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MagnifyingGlass, ArrowClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const ENTITY_LABEL = {
  receiving: "Receiving",
  delivery: "Delivery",
  customer: "Customer",
  user: "User",
};

const ACTION_LABEL = {
  update: { l: "Ubah", tone: "bg-sky-50 text-sky-800 border-sky-200" },
  delete: { l: "Hapus", tone: "bg-red-50 text-red-800 border-red-200" },
  role_change: { l: "Ubah Role", tone: "bg-indigo-50 text-indigo-800 border-indigo-200" },
  password_reset: { l: "Reset Password", tone: "bg-amber-50 text-amber-800 border-amber-200" },
};

/** Compare before/after and return a diff summary [{key, from, to}]. */
function diffOf(before, after) {
  const b = before || {};
  const a = after || {};
  const keys = Array.from(new Set([...Object.keys(b), ...Object.keys(a)]));
  const skip = new Set(["_id", "updated_at", "updated_by", "updated_by_name", "created_at", "created_by"]);
  return keys
    .filter((k) => !skip.has(k))
    .map((k) => ({ key: k, from: b[k], to: a[k] }))
    .filter((d) => JSON.stringify(d.from) !== JSON.stringify(d.to));
}

const fmtVal = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

export default function EditLogPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [entity, setEntity] = useState("all");
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (entity !== "all") params.entity = entity;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await api.get("/edit-logs", { params });
      setRows(data || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };
  useAutoRefresh(load, { deps: [entity] });

  const filtered = rows.filter((r) => {
    if (!q.trim()) return true;
    const hay = `${r.entity} ${r.action} ${r.changed_by} ${r.changed_by_name} ${JSON.stringify(r.before)} ${JSON.stringify(r.after)}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  return (
    <AppShell
      title="Riwayat Perubahan Data"
      subtitle="Audit trail seluruh aksi edit / hapus / ubah role / reset password. Khusus owner."
      actions={
        <Button onClick={load} data-testid="editlog-refresh" className="bg-slate-900 hover:bg-slate-800 h-10 gap-2">
          <ArrowClockwise size={15} weight="bold" /> <span className="hidden sm:inline">Muat Ulang</span>
        </Button>
      }
    >
      <div className="rounded-xl bg-white border border-slate-200 p-4 mb-4 shadow-sm">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Field label="Entitas">
            <Select value={entity} onValueChange={setEntity}>
              <SelectTrigger data-testid="editlog-entity" className="h-10"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua</SelectItem>
                <SelectItem value="receiving">Receiving</SelectItem>
                <SelectItem value="delivery">Delivery</SelectItem>
                <SelectItem value="customer">Customer</SelectItem>
                <SelectItem value="user">User</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Dari Tanggal">
            <Input data-testid="editlog-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-10" />
          </Field>
          <Field label="Sampai Tanggal">
            <Input data-testid="editlog-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-10" />
          </Field>
          <Field label="Kata Kunci">
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="editlog-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="siapa / apa yang berubah…"
                className="h-10 pl-9"
              />
            </div>
          </Field>
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <Button variant="outline" onClick={() => { setEntity("all"); setDateFrom(""); setDateTo(""); setQ(""); setTimeout(load, 0); }} className="h-9" data-testid="editlog-reset">Reset</Button>
          <Button onClick={load} className="h-9 bg-sky-600 hover:bg-sky-700 gap-2" data-testid="editlog-apply">
            <MagnifyingGlass size={13} weight="bold" /> Cari
          </Button>
        </div>
      </div>

      <div className="rounded-xl bg-white border border-slate-200 overflow-hidden shadow-sm">
        <div className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-3 bg-slate-50">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Log Perubahan</span>
          <span className="ml-auto text-[11px] font-mono text-slate-500" data-testid="editlog-count">
            {loading ? "Memuat…" : `${filtered.length} baris`}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-3 py-2 font-heading text-[10.5px] font-bold uppercase tracking-wider text-slate-600">Waktu</th>
                <th className="text-left px-3 py-2 font-heading text-[10.5px] font-bold uppercase tracking-wider text-slate-600">Entitas</th>
                <th className="text-left px-3 py-2 font-heading text-[10.5px] font-bold uppercase tracking-wider text-slate-600">Aksi</th>
                <th className="text-left px-3 py-2 font-heading text-[10.5px] font-bold uppercase tracking-wider text-slate-600">Siapa</th>
                <th className="text-left px-3 py-2 font-heading text-[10.5px] font-bold uppercase tracking-wider text-slate-600">Perubahan</th>
              </tr>
            </thead>
            <tbody data-testid="editlog-table">
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={5} className="text-center py-10 text-slate-400 text-[13px]">Tidak ada log untuk filter ini.</td></tr>
              )}
              {filtered.map((r) => {
                const diffs = diffOf(r.before, r.after);
                const action = ACTION_LABEL[r.action] || { l: r.action, tone: "bg-slate-100 text-slate-700 border-slate-200" };
                return (
                  <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setDetail(r)}>
                    <td className="px-3 py-2 align-top font-mono text-[11px] whitespace-nowrap">
                      {new Date(r.created_at).toLocaleString("id-ID")}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <span className="inline-block px-2 py-0.5 rounded-md border text-[11px] font-semibold bg-slate-50 text-slate-800 border-slate-200">
                        {ENTITY_LABEL[r.entity] || r.entity}
                      </span>
                    </td>
                    <td className="px-3 py-2 align-top">
                      <span className={`inline-block px-2 py-0.5 rounded-md border text-[11px] font-semibold ${action.tone}`}>{action.l}</span>
                    </td>
                    <td className="px-3 py-2 align-top">
                      <div className="text-slate-800 font-semibold">{r.changed_by_name || "—"}</div>
                      <div className="text-[10.5px] text-slate-500 font-mono">{r.changed_by} · {r.changed_by_role}</div>
                    </td>
                    <td className="px-3 py-2 align-top">
                      {diffs.length === 0 ? (
                        <span className="text-slate-400 text-[11px] italic">(tidak ada diff)</span>
                      ) : (
                        <div className="space-y-0.5 max-w-md">
                          {diffs.slice(0, 3).map((d) => (
                            <div key={d.key} className="text-[11.5px]">
                              <b className="text-slate-700">{d.key}</b>: <span className="line-through text-red-600 font-mono">{fmtVal(d.from)}</span> <span className="text-slate-400">→</span> <span className="text-emerald-700 font-mono">{fmtVal(d.to)}</span>
                            </div>
                          ))}
                          {diffs.length > 3 && (
                            <div className="text-[10px] text-slate-500 font-mono">+ {diffs.length - 3} field lainnya…</div>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">Detail Perubahan</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-4 text-[12px]">
                <Info label="Waktu" value={new Date(detail.created_at).toLocaleString("id-ID")} />
                <Info label="Entitas" value={ENTITY_LABEL[detail.entity] || detail.entity} />
                <Info label="ID" value={detail.entity_id} mono />
                <Info label="Aksi" value={ACTION_LABEL[detail.action]?.l || detail.action} />
                <Info label="Diubah oleh" value={`${detail.changed_by_name} (${detail.changed_by})`} />
                <Info label="Role pengubah" value={detail.changed_by_role} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="rounded-md border border-red-200 bg-red-50/40 p-3">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-red-800 mb-2">Sebelum</div>
                  <pre className="text-[11px] font-mono text-slate-700 whitespace-pre-wrap break-all max-h-64 overflow-y-auto">{JSON.stringify(detail.before, null, 2)}</pre>
                </div>
                <div className="rounded-md border border-emerald-200 bg-emerald-50/40 p-3">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-800 mb-2">Sesudah</div>
                  <pre className="text-[11px] font-mono text-slate-700 whitespace-pre-wrap break-all max-h-64 overflow-y-auto">{JSON.stringify(detail.after, null, 2)}</pre>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-slate-500">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Info({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-slate-800 mt-0.5 ${mono ? "font-mono text-[11px] break-all" : ""}`}>{value || "—"}</div>
    </div>
  );
}
